import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";

import {
  getInvoices,
  getLastInvoiceSummary,
  generateInvoice,
  downloadInvoice,
  createOrder,
} from "../services/billingService";

const BillingContext = createContext(null);

/* ===================================================== */
/* PROVIDER                                              */
/* ===================================================== */

export const BillingProvider = ({ children }) => {
  const [invoices, setInvoices] = useState([]);
  const [lastInvoice, setLastInvoice] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [selectedPlan, setSelectedPlan] = useState("free");

  /* ===================================================== */
  /* 📦 Load Invoices                                      */
  /* ===================================================== */

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await getInvoices();
      const list = Array.isArray(data) ? data : [];

      // sort latest first
      list.sort(
        (a, b) =>
          new Date(b.createdAt || 0) -
          new Date(a.createdAt || 0)
      );

      setInvoices(list);
      return list;
    } catch (e) {
      setError(e.message || "Failed to load invoices");
      setInvoices([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /* ===================================================== */
  /* 📊 Load Last Invoice                                  */
  /* ===================================================== */

  const loadLastInvoice = useCallback(async () => {
    try {
      const data = await getLastInvoiceSummary();
      setLastInvoice(data || null);
      return data;
    } catch (e) {
      console.error("Last invoice fetch failed:", e.message);
      return null;
    }
  }, []);

  /* ===================================================== */
  /* 🧾 Generate Invoice                                  */
  /* ===================================================== */

  const createInvoice = useCallback(async (plan) => {
    if (!plan) throw new Error("Plan is required");

    setLoading(true);
    setError("");

    try {
      const invoice = await generateInvoice(plan);

      setInvoices((prev) => [invoice, ...prev]);
      setLastInvoice(invoice);
      setSelectedPlan(plan);

      return invoice;
    } catch (e) {
      setError(e.message || "Failed to generate invoice");
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  /* ===================================================== */
  /* 📄 Download Invoice                                  */
  /* ===================================================== */

  const download = useCallback(async (invoiceId) => {
    if (!invoiceId) throw new Error("Invoice ID required");

    try {
      await downloadInvoice(invoiceId);
    } catch (e) {
      setError(e.message || "Failed to download invoice");
      throw e;
    }
  }, []);

  /* ===================================================== */
  /* 💳 Create Payment Order (Razorpay)                   */
  /* ===================================================== */

  const startPayment = useCallback(async ({ plan, amount }) => {
    if (!plan || !amount) {
      throw new Error("Plan and amount required");
    }

    setLoading(true);
    setError("");

    try {
      const order = await createOrder({ plan, amount });
      setSelectedPlan(plan);
      return order;
    } catch (e) {
      setError(e.message || "Payment initiation failed");
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  /* ===================================================== */
  /* 📊 Subscription Summary                              */
  /* ===================================================== */

  const subscriptionSummary = useCallback(() => {
    const total = invoices.length;

    const paid = invoices.filter(
      (i) => i.status === "paid"
    ).length;

    const pending = total - paid;

    return {
      total,
      paid,
      pending,
      lastInvoice,
      currentPlan: selectedPlan,
    };
  }, [invoices, lastInvoice, selectedPlan]);

  /* ===================================================== */
  /* 🌐 Context Value (MEMOIZED)                          */
  /* ===================================================== */

  const value = useMemo(
    () => ({
      invoices,
      lastInvoice,
      loading,
      error,

      selectedPlan,
      setSelectedPlan,

      loadInvoices,
      loadLastInvoice,

      createInvoice,
      downloadInvoice: download,

      startPayment,

      subscriptionSummary,
    }),
    [
      invoices,
      lastInvoice,
      loading,
      error,
      selectedPlan,
      loadInvoices,
      loadLastInvoice,
      createInvoice,
      download,
      startPayment,
      subscriptionSummary,
    ]
  );

  return (
    <BillingContext.Provider value={value}>
      {children}
    </BillingContext.Provider>
  );
};

/* ===================================================== */
/* HOOK                                                  */
/* ===================================================== */

export const useBilling = () => {
  const ctx = useContext(BillingContext);

  if (!ctx) {
    throw new Error(
      "useBilling must be used within BillingProvider"
    );
  }

  return ctx;
};